import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { AlertCircle, Loader2 } from 'lucide-react';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';

const Payment = () => {
  const { entryId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [entry, setEntry] = useState<any>(null);
  const [campaign, setCampaign] = useState<any>(null);
  const [paypalOrderId, setPaypalOrderId] = useState<string | null>(null);

  useEffect(() => {
    if (!entryId) {
      setError('Invalid entry ID');
      setLoading(false);
      return;
    }
    fetchEntryDetails();
  }, [entryId]);

  const fetchEntryDetails = async () => {
    try {
      // Fetch entry details
      const { data: entryData, error: entryError } = await supabase
        .from('entries')
        .select('*, campaigns(*)')
        .eq('id', entryId)
        .single();

      if (entryError) throw entryError;

      if (!entryData) {
        throw new Error('Entry not found');
      }

      if (entryData.payment_status === 'completed') {
        throw new Error('This entry has already been paid for');
      }

      setEntry(entryData);
      setCampaign(entryData.campaigns);

      // Create PayPal order
      const response = await fetch('/api/paypal/create-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: entryData.campaigns.ticket_price.toFixed(2),
          currency: 'USD',
        }),
      });

      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }

      setPaypalOrderId(data.orderId);
    } catch (error: any) {
      console.error('Error:', error);
      setError(error.message || 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalApprove = async () => {
    if (!paypalOrderId || !entry || !campaign) return;

    try {
      setLoading(true);
      const response = await fetch(`/api/paypal/capture-order/${paypalOrderId}`, {
        method: 'POST',
      });

      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }

      // Generate ticket number
      const prefix = campaign.id.slice(0, 4).toUpperCase();
      const random = Math.floor(100000 + Math.random() * 900000).toString();
      const ticketNumber = `${prefix}-${random}`;

      // Update entry with ticket number and payment status
      const { error: updateError } = await supabase
        .from('entries')
        .update({
          ticket_number: ticketNumber,
          payment_status: 'completed'
        })
        .eq('id', entryId);

      if (updateError) throw updateError;

      // Send confirmation SMS
      await fetch('/api/send-confirmation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: entry.phone_number,
          ticketNumber,
          campaignName: campaign.name,
        }),
      });

      // Redirect to success page with ticket number
      navigate(`/success/${ticketNumber}`);
    } catch (error: any) {
      console.error('Error:', error);
      setError(error.message || 'Failed to complete payment');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg p-8 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-purple-600" />
        <p>Processing payment...</p>
      </div>
    );
  }

  if (error || !entry || !campaign) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg p-8">
        <div className="flex items-center justify-center text-red-600 mb-4">
          <AlertCircle className="h-12 w-12" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 text-center mb-4">Payment Error</h2>
        <p className="text-gray-600 text-center mb-6">{error}</p>
        <button
          onClick={() => navigate('/')}
          className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 transition-colors"
        >
          Return Home
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg p-8">
      <h2 className="text-2xl font-bold text-gray-800 text-center mb-6">Complete Your Payment</h2>

      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-gray-600">Entry ID:</span>
          <span className="font-semibold">{entry.id}</span>
        </div>
        <div className="flex justify-between items-center mb-2">
          <span className="text-gray-600">Campaign:</span>
          <span className="font-semibold">{campaign.name}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Amount:</span>
          <span className="font-semibold">${campaign.ticket_price.toFixed(2)}</span>
        </div>
      </div>

      <PayPalScriptProvider options={{
        clientId: import.meta.env.VITE_PAYPAL_CLIENT_ID || "AZsQrvQczN2t0UVATlz3vyvuAdIJ-Gu9mkpKToD2rSoFPFh8edb1FEw-Q4_uk5Z4FSr7TtqhKfVCYnVg",
        currency: "USD"
      }}>
        <PayPalButtons
          createOrder={async () => {
            if (!paypalOrderId) {
              throw new Error('No order ID available');
            }
            return paypalOrderId;
          }}
          onApprove={handlePayPalApprove}
          onError={(err) => {
            console.error('PayPal Error:', err);
            setError('Payment failed. Please try again.');
          }}
          style={{ layout: "vertical" }}
        />
      </PayPalScriptProvider>

      <div className="text-sm text-gray-500 text-center mt-6">
        Your payment is secure and encrypted
      </div>
    </div>
  );
};

export default Payment;